import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { 
  CalendarIcon,
  ClockIcon,
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  EyeIcon,
  CheckCircleIcon,
  XIcon
} from "lucide-react";
import { toast } from "sonner";
import { mockData } from "../data/mock";
import { bookingService, doctorService, clinicService } from "../services";
import { openWhatsApp } from "../lib/utils";

const BookAppointmentModal = ({ 
  children, 
  treatmentType = "general-consultation", 
  doctorName = "",
  isOpen,
  onOpenChange 
}) => {
  const [formData, setFormData] = useState({
    patientName: "",
    patientMobile: "",
    patientEmail: "",
    patientAge: "",
    patientGender: "",
    city: "all-cities",
    treatmentType: treatmentType,
    doctor: doctorName,
    preferredDate: "",
    preferredTime: "",
    symptoms: "",
    previousTreatment: ""
  });

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Data states with fallbacks to demo data
  const [cities, setCities] = useState(["Faridabad", "Delhi", "Gurgaon", "Noida"]);
  // Get only treatments that have existing pages
  const availableTreatments = [
    // Diseases with existing pages
    "Piles", "Hernia", "Gall stones", "Kidney stone", "Circumcision",
    // Eye treatments with existing pages
    "Cataract Surgery", "LASIK Eye Surgery", "Femto LASIK Surgery", "SMILE LASIK Surgery",
    "Glaucoma Surgery", "Diabetic Retinopathy", "Retinal Detachment", "ICL Surgery", 
    "Vitrectomy Surgery", "PRK LASIK Surgery", "Contoura Vision", "Phaco Surgery", "Squint Surgery"
  ];
  const [treatments, setTreatments] = useState(availableTreatments);
  const [timeSlots, setTimeSlots] = useState(mockData.timeSlots || []);
  const [doctors, setDoctors] = useState(mockData.doctors || []);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Required fields for booking
  const requiredFields = ['patientName', 'patientMobile', 'patientEmail', 'patientAge', 'patientGender', 'city', 'treatmentType', 'preferredDate', 'preferredTime'];

  // Load data from APIs with fallback to demo data
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingData(true);
      
      try {
        // Try to load doctors from API
        if (doctorService && doctorService.getAllDoctors) {
          const doctorsData = await doctorService.getAllDoctors();
          if (Array.isArray(doctorsData?.data)) {
            setDoctors(doctorsData.data);
          } else if (doctorsData?.result?.doctors && Array.isArray(doctorsData.result.doctors)) {
            setDoctors(doctorsData.result.doctors);
          } else if (Array.isArray(doctorsData?.docs)) {
            setDoctors(doctorsData.docs);
          }
        }
        
        // Try to load clinics from API
        if (clinicService && clinicService.getAllClinics) {
          const clinicsData = await clinicService.getAllClinics();
          // Handle clinic data if needed
        }
        
      } catch (error) {
        console.warn('Failed to load data from APIs, using demo data:', error);
        // Keep using demo data fallbacks
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, []);

  const handleInputChange = (field, value) => {
    console.log(`Input change - Field: ${field}, Value: ${value}`);
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      console.log('Updated form data:', newData);
      return newData;
    });
  };

  const validateStep1 = () => {
    return formData.patientName && formData.patientMobile && formData.patientEmail && formData.patientAge && formData.patientGender;
  };

  const validateStep2 = () => {
    return formData.treatmentType && formData.preferredDate && formData.preferredTime;
  };

  const validateRequiredFields = () => {
    const missingFields = requiredFields.filter(field => !formData[field] || formData[field] === '');
    console.log('Validation - Form data:', formData);
    console.log('Validation - Missing fields:', missingFields);
    console.log('Validation - Required fields:', requiredFields);
    return missingFields.length === 0;
  };

  const handleNext = () => {
    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      } else {
        toast.error("Please fill all required fields");
      }
    } else if (step === 2) {
      if (validateStep2()) {
        // Step 2 is the final step, so we don't need to go to step 3
        // The form submission will be handled by the submit button
        console.log('Step 2 validation passed, ready for submission');
      } else {
        toast.error("Please fill all required fields");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submitted, current form data:', formData);
    
    if (!validateRequiredFields()) {
      const missingFields = requiredFields.filter(field => !formData[field] || formData[field] === '');
      console.log('Validation failed, missing fields:', missingFields);
      toast.error(`Please complete all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    console.log('Validation passed, proceeding with submission');

    setIsSubmitting(true);
    
    // Submit booking to API
    try {
      const bookingData = {
        patientName: formData.patientName,
        patientMobile: formData.patientMobile,
        patientEmail: formData.patientEmail,
        patientAge: formData.patientAge,
        patientGender: formData.patientGender,
        city: formData.city,
        treatmentType: formData.treatmentType,
        doctor: formData.doctor === '__no_preference__' ? '' : formData.doctor,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        symptoms: formData.symptoms,
        previousTreatment: formData.previousTreatment
      };
      
      console.log('Form data before submission:', formData);
      console.log('Submitting booking data:', bookingData);
      console.log('Patient Name in form data:', formData.patientName);
      console.log('Patient Name in booking data:', bookingData.patientName);
      console.log('All form data keys:', Object.keys(formData));
      console.log('All booking data keys:', Object.keys(bookingData));
      
      const result = await bookingService.createBooking(bookingData);
      console.log('Booking API response:', result);
      
      toast.success("Appointment booked successfully! Our team will contact you soon.");
      
      // Reset form
      setFormData({
        patientName: "",
        patientMobile: "",
        patientEmail: "",
        patientAge: "",
        patientGender: "",
        city: "all-cities",
        treatmentType: treatmentType,
        doctor: doctorName,
        preferredDate: "",
        preferredTime: "",
        symptoms: "",
        previousTreatment: ""
      });
      setStep(1);
      
      // Close modal if controlled
      if (onOpenChange) {
        onOpenChange(false);
      }
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      
      // Create a detailed WhatsApp message with the booking details
      const whatsappMessage = `Hi! I want to book an appointment for ${formData.treatmentType}.

Patient Details:
• Name: ${formData.patientName}
• Mobile: ${formData.patientMobile}
• Email: ${formData.patientEmail}
• Age: ${formData.patientAge}
• Gender: ${formData.patientGender}
• City: ${formData.city}
• Preferred Date: ${formData.preferredDate}
• Preferred Time: ${formData.preferredTime}
${formData.symptoms ? `• Symptoms: ${formData.symptoms}` : ''}
${formData.previousTreatment ? `• Previous Treatment: ${formData.previousTreatment}` : ''}

Please confirm my appointment. Thank you!`;
      
      // Show error but offer WhatsApp alternative
      toast.error("Unable to book online. Opening WhatsApp for manual booking...", {
        duration: 5000,
        action: {
          label: "Open WhatsApp",
          onClick: () => openWhatsApp("9311720045", whatsappMessage)
        }
      });
      
      // Automatically open WhatsApp after a short delay
      setTimeout(() => {
        openWhatsApp("9311720045", whatsappMessage);
        
        // Reset form after WhatsApp opens
        setFormData({
          patientName: "",
          patientMobile: "",
          patientEmail: "",
          patientAge: "",
          patientGender: "",
          city: "all-cities",
          treatmentType: treatmentType,
          doctor: doctorName,
          preferredDate: "",
          preferredTime: "",
          symptoms: "",
          previousTreatment: ""
        });
        setStep(1);
        
        // Close modal if controlled
        if (onOpenChange) {
          onOpenChange(false);
        }
      }, 2000);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setFormData({
      patientName: "",
      patientMobile: "",
      patientEmail: "",
      patientAge: "",
      patientGender: "",
      city: "all-cities",
      treatmentType: treatmentType,
      doctor: doctorName,
      preferredDate: "",
      preferredTime: "",
      symptoms: "",
      previousTreatment: ""
    });
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const modalContent = (
    <DialogContent 
      className="w-[95vw] max-w-4xl h-[90vh] max-h-[800px] overflow-hidden flex flex-col p-4 sm:p-6"
      style={{
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'fixed'
      }}
    >
      <DialogHeader className="flex-shrink-0 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                Book Free Consultation
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-gray-600">
                Schedule your appointment with our expert specialists
              </DialogDescription>
            </div>
          </div>
          {onOpenChange && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={resetAndClose}
              className="h-8 w-8 p-0"
            >
              <XIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </DialogHeader>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto px-1">
        {/* Quick WhatsApp Booking Option */}
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-green-800">Need immediate assistance?</p>
              <p className="text-xs text-green-600">Book instantly via WhatsApp</p>
            </div>
            <Button 
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
              onClick={() => {
                const quickMessage = `Hi! I need to book an appointment for ${treatmentType || 'general consultation'}. Please help me with the booking process.`;
                openWhatsApp("9311720045", quickMessage);
                if (onOpenChange) {
                  onOpenChange(false);
                }
              }}
            >
              <PhoneIcon className="w-4 h-4 mr-1" />
              Quick Book
            </Button>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center space-x-2 sm:space-x-4 mb-4 sm:mb-6">
          <div className={`flex items-center space-x-1 sm:space-x-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
              {step > 1 ? <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4" /> : <span className="text-xs sm:text-sm font-medium">1</span>}
            </div>
            <span className="text-xs sm:text-sm font-medium hidden sm:inline">Personal Info</span>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'} rounded`}></div>
          <div className={`flex items-center space-x-1 sm:space-x-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
              <span className="text-xs sm:text-sm font-medium">2</span>
            </div>
            <span className="text-xs sm:text-sm font-medium hidden sm:inline">Appointment</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {step === 1 && (
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Personal Information</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                 <Input
                   value={formData.patientName}
                   onChange={(e) => handleInputChange('patientName', e.target.value)}
                   placeholder="Enter your full name"
                   required
                 />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                 <Input
                   value={formData.patientMobile}
                   onChange={(e) => handleInputChange('patientMobile', e.target.value)}
                   placeholder="Enter mobile number"
                   required
                 />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
               <Input
                 type="email"
                 value={formData.patientEmail}
                 onChange={(e) => handleInputChange('patientEmail', e.target.value)}
                 placeholder="Enter email address"
                 required
               />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age *
                </label>
                 <Input
                   type="number"
                   value={formData.patientAge}
                   onChange={(e) => handleInputChange('patientAge', e.target.value)}
                   placeholder="Enter age"
                   min="1"
                   max="120"
                   required
                 />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                 <Select value={formData.patientGender} onValueChange={(value) => handleInputChange('patientGender', value)}>
                   <SelectTrigger>
                     <SelectValue placeholder="Select gender" />
                   </SelectTrigger>
                   <SelectContent>
                     <SelectItem value="male">Male</SelectItem>
                     <SelectItem value="female">Female</SelectItem>
                     <SelectItem value="other">Other</SelectItem>
                   </SelectContent>
                 </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <Select value={formData.city} onValueChange={(value) => handleInputChange('city', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-cities">Select City</SelectItem>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
              </Select>
            </div>

              <div className="flex justify-end pt-2">
                <Button type="button" onClick={handleNext} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                  Next Step
                </Button>
              </div>
          </div>
        )}

          {step === 2 && (
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Appointment Details</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Treatment Required *
              </label>
               <Select value={formData.treatmentType} onValueChange={(value) => handleInputChange('treatmentType', value)}>
                 <SelectTrigger>
                   <SelectValue placeholder="Select treatment" />
                 </SelectTrigger>
                 <SelectContent>
                   <SelectItem value="general-consultation">General Consultation</SelectItem>
                   {treatments.map(treatment => (
                     <SelectItem key={treatment} value={treatment}>{treatment}</SelectItem>
                   ))}
                 </SelectContent>
               </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Doctor (Optional)
              </label>
              <Select value={formData.doctor} onValueChange={(value) => handleInputChange('doctor', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select doctor (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__no_preference__">No preference</SelectItem>
                  {doctorName && !doctors.some(d => (d.fullName || d.name) === doctorName) && (
                    <SelectItem value={doctorName}>{doctorName}</SelectItem>
                  )}
                  {doctors.map((doctor, index) => (
                    <SelectItem key={doctor._id || doctor.id || index} value={doctor.fullName || doctor.name}>
                      {(doctor.fullName || doctor.name)} - {doctor.experience} years ({doctor.specialty})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date *
                  </label>
                <Input
                  type="date"
                  value={formData.preferredDate}
                  onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time *
                </label>
                <Select value={formData.preferredTime} onValueChange={(value) => handleInputChange('preferredTime', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Symptoms or Concerns
              </label>
              <Textarea
                value={formData.symptoms}
                onChange={(e) => handleInputChange('symptoms', e.target.value)}
                placeholder="Describe your symptoms or concerns..."
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Previous Eye Treatment
              </label>
              <Textarea
                value={formData.previousTreatment}
                onChange={(e) => handleInputChange('previousTreatment', e.target.value)}
                placeholder="Any previous eye treatments or surgeries..."
                rows={2}
              />
            </div>

              <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="w-full sm:w-auto"
                >
                  Back
                </Button>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <Button 
                    type="submit" 
                    className="bg-gray-900 hover:bg-gray-600 w-full sm:w-auto"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Booking...
                      </>
                    ) : (
                      <>
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Book Appointment
                      </>
                    )}
                  </Button>
                  
                  {/* WhatsApp Alternative Button */}
                  <Button 
                    type="button"
                    variant="outline"
                    className="border-green-600 text-green-600 hover:bg-green-50 w-full sm:w-auto"
                    onClick={() => {
                      const whatsappMessage = `Hi! I want to book an appointment for ${formData.treatmentType || 'general consultation'}.

Patient Details:
• Name: ${formData.patientName || '[Please provide]'}
• Mobile: ${formData.patientMobile || '[Please provide]'}
• Email: ${formData.patientEmail || '[Please provide]'}
• Age: ${formData.patientAge || '[Please provide]'}
• Gender: ${formData.patientGender || '[Please provide]'}
• City: ${formData.city || '[Please provide]'}
• Preferred Date: ${formData.preferredDate || '[Please provide]'}
• Preferred Time: ${formData.preferredTime || '[Please provide]'}
${formData.symptoms ? `• Symptoms: ${formData.symptoms}` : ''}
${formData.previousTreatment ? `• Previous Treatment: ${formData.previousTreatment}` : ''}

Please confirm my appointment. Thank you!`;
                      
                      openWhatsApp("9311720045", whatsappMessage);
                      
                      // Close modal after WhatsApp opens
                      if (onOpenChange) {
                        onOpenChange(false);
                      }
                    }}
                  >
                    <PhoneIcon className="w-4 h-4 mr-2" />
                    Book via WhatsApp
                  </Button>
                </div>
              </div>
          </div>
        )}
          </form>

          {/* Benefits */}
          <Card className="mt-4 sm:mt-6 bg-blue-50 border-blue-200">
            <CardContent className="p-3 sm:p-4">
              <h4 className="font-semibold text-blue-900 mb-2 sm:mb-3 text-sm sm:text-base">Why Choose Our Consultation?</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-xs sm:text-sm text-blue-800">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                  <span>Free consultation</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                  <span>Expert doctors</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                  <span>Advanced medical equipment</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                  <span>Same-day appointments</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                  <span>WhatsApp booking available</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                  <span>Quick response guaranteed</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
    </DialogContent>
  );

  // If controlled (isOpen and onOpenChange provided), don't wrap in Dialog
  if (isOpen !== undefined && onOpenChange) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        {modalContent}
      </Dialog>
    );
  }

  // If uncontrolled, wrap in Dialog with trigger
  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      {modalContent}
    </Dialog>
  );
};

export default BookAppointmentModal;